import { Customer, DocumentDetails, DocumentType, TaxDetails, Traveler } from '@/src/types/itinerary'
import * as TravelerDocumentUtils from '@/src/utils/itinerary/travelerDocumentToInputsSegregater'
import { ValidationHookReturn } from '@/src/utils/itinerary/travelerInputValidationRules'
import { useCallback } from 'react'

export const useValidationHelpers = ({
    travelers,
    travelerValidation,
    contactValidation,
    taxValidation,
    contactDetails,
    taxDetails,
    isBirthCountryRequired,
    isNationalityRequiredToRenderInUserPersonalDetailsSection,
    isDateOfBirthRequired
}: {
    travelers: Traveler[]
    travelerValidation: ValidationHookReturn
    contactValidation: ValidationHookReturn
    taxValidation: ValidationHookReturn
    contactDetails: Customer
    taxDetails: TaxDetails
    isBirthCountryRequired: boolean
    isNationalityRequiredToRenderInUserPersonalDetailsSection: boolean
    isDateOfBirthRequired: boolean
}) => {
    // Combined validation helpers
    const validateAllForms = useCallback(() => {
        const travelerValues = travelers.reduce(
            (acc, traveler, index) => {
                acc[`traveler_${index}_firstName`] = traveler.firstName
                acc[`traveler_${index}_lastName`] = traveler.lastName
                acc[`traveler_${index}_title`] = traveler.title
                if (isNationalityRequiredToRenderInUserPersonalDetailsSection) {
                    acc[`traveler_${index}_nationality`] = traveler.nationality
                }

                const isDobNeededForTraveler =
                    isDateOfBirthRequired || traveler.paxType === 'CHD' || traveler.paxType === 'INF'
                if (isDobNeededForTraveler) {
                    acc[`traveler_${index}_dateOfBirth`] = traveler.dateOfBirth
                }
                if (isBirthCountryRequired) acc[`traveler_${index}_birthCountry`] = traveler.birthCountry

                // Add document details
                const docType = traveler.selectedDocumentType
                const details = traveler.documentDetails?.[docType!]
                const documentInputs = TravelerDocumentUtils.travelerDocumentToInputsSegregate(docType!)

                // Add docNumber if it's required for this document type
                if (documentInputs.docNumber) {
                    acc[`traveler_${index}_${docType}_docNumber`] = details.docNumber
                }

                // Add nationality if it's required for this document type
                if (documentInputs.nationality) {
                    acc[`traveler_${index}_${docType}_nationality`] = details.nationality
                }

                // Add countryIssued if it's required for this document type
                if (documentInputs.countryIssued) {
                    acc[`traveler_${index}_${docType}_countryIssued`] = details.countryIssued
                }

                // Add expiryDate if it's required for this document type
                if (documentInputs.expiryDate) {
                    acc[`traveler_${index}_${docType}_expiryDate`] = details.expiryDate
                }

                // Add issueDate if it's required for this document type
                if (documentInputs.issueDate) {
                    acc[`traveler_${index}_${docType}_issueDate`] = details.issueDate
                }
                return acc
            },
            {} as Record<string, any>
        )

        const contactValues = {
            email: contactDetails.email,
            mobile: {
                mobile: contactDetails.mobile,
                mobileCountryCode: contactDetails.mobileCountryCode
            },
            mobileCountryCode: contactDetails.mobileCountryCode
        }

        const taxValues = {
            companyName: taxDetails.companyName,
            taxNo: taxDetails.taxNo
        }

        const travelerValid = travelerValidation.validateAll(travelerValues)
        const contactValid = contactValidation.validateAll(contactValues)
        const taxValid = taxDetails.addTaxChecked ? taxValidation.validateAll(taxValues) : true

        return travelerValid && contactValid && taxValid
    }, [travelers, contactDetails, taxDetails, travelerValidation, contactValidation, taxValidation])

    // Function to validate a specific traveler
    const validateTraveler = useCallback(
        (travelerIndex: number): boolean => {
            const traveler = travelers[travelerIndex]
            if (!traveler) return false

            const travelerPrefix = `traveler_${travelerIndex}_`
            const validationState = travelerValidation.validationState
            // Get all validation keys for this traveler
            const travelerValidationKeys = Object.keys(validationState).filter(key => key.startsWith(travelerPrefix))
            let hasErrors = false

            // Validate each field individually and update state
            travelerValidationKeys.forEach(fieldKey => {
                const fieldName = fieldKey.replace(travelerPrefix, '')
                let value: any = undefined

                // Handle personal details
                if (fieldName === 'firstName') {
                    value = traveler.firstName
                } else if (fieldName === 'lastName') {
                    value = traveler.lastName
                } else if (fieldName === 'title') {
                    value = traveler.title
                } else if (fieldName === 'nationality') {
                    value = traveler.nationality
                } else if (fieldName === 'dateOfBirth') {
                    value = traveler.dateOfBirth
                } else if (fieldName === 'birthCountry') {
                    value = traveler.birthCountry
                } else {
                    // Handle document fields (e.g., PASSPORT_docNumber, PASSPORT_nationality, etc.)
                    const [docType, docField] = fieldName.split('_')
                    if (
                        docType &&
                        docType === traveler.selectedDocumentType &&
                        docField &&
                        traveler.documentDetails[docType as DocumentType]
                    ) {
                        value = traveler.documentDetails[docType as DocumentType][docField as keyof DocumentDetails]
                    }
                }

                // Validate the specific field and check for errors synchronously
                if (value !== undefined) {
                    // const fieldConfig = validationState[fieldKey]
                    // if (fieldConfig) {
                    // let errorMessage = ''
                    // Run all validation rules
                    // for (const rule of fieldConfig.rules) {
                    //     const result = rule(value)
                    //     if (result) {
                    //         errorMessage = result
                    //         hasErrors = true
                    //         break // Stop at first error
                    //     }
                    // }

                    const isValid = travelerValidation.validateField(fieldKey, value)
                    if (!isValid) {
                        hasErrors = true
                    }
                    // }
                }
            })

            return !hasErrors
        },
        [travelers, travelerValidation]
    )

    return {
        validateAllForms,
        validateTraveler
    }
}
